import React, { memo } from 'react';
import { useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useStyles } from '../DiagnosticRevisionTable.style';
import { Tooltip, Typography, IconButton } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import Icon from '@iconify/react';
import linkIcon from '@iconify-icons/mdi/link-variant';
import possibleLinkIcon from '@iconify-icons/mdi/link-variant-plus';
import unMatchIcon from '@iconify-icons/mdi/link-variant-off';
import LensIcon from '@material-ui/icons/Lens';
import modelDetectedIcon from '../../../../../../resources/coorporate_images/brayz_logo_small.png';
import { getLesionClass } from '../utils/utils';
import AddIcon from '@iconify-icons/mdi/user-add';
import EditIcon from '@iconify-icons/mdi/user-edit';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';

const IndexCell = ({
  lesionAllInfo,
  isClustered,
  isMatchedMainRow,
  isExpanded,
  setExpandedClusters,
  nested,
  isHovered,
  lesionId,
  findingId,
  matched,
  isPossibleMatch,
  isDraggableDisabled,
  justEditedRows,
  onClassChange,
  onUnmatch,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();

  // Derived flags (makes logic readable)

  const isExpandable = isMatchedMainRow || isClustered;
  const isDraggable =
    !isExpandable &&
    isHovered &&
    !nested &&
    lesionAllInfo?.module_type !== 'microcalc' &&
    !isDraggableDisabled;

  const isJustEdited = [lesionId, findingId].some((id) => {
    if (id == null || id === '') return false;
    if (id === justEditedRows) return true;
    if (justEditedRows == null || justEditedRows === '') return false;
    const idStr = String(id);
    const editedStr = String(justEditedRows);
    return idStr.includes(editedStr) || editedStr.includes(idStr);
  });

  // Handlers

  const toggleExpand = (e) => {
    if (!setExpandedClusters) return;

    if (e.target.closest('.row_actions_icon') || e.target.closest('.matched_icon')) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    setExpandedClusters((prev) => ({
      ...prev,
      [lesionId]: !prev[lesionId],
    }));
  };

  const handleUnmatchClick = (e, lesionAllInfo) => {
    e.stopPropagation();
    onUnmatch(lesionAllInfo);
    e.preventDefault();
  };

  // Extra Info Icon (bottom right) and Tootlip
  const renderExtraInfo = () => {
    if (!lesionAllInfo || lesionAllInfo?.clustered) {
      return { infoIcon: null, infoTooltip: '' };
    }

    const matched = lesionAllInfo?.matched_lesions || [];

    // NEW lesion detection
    const isNewLesion = lesionAllInfo?.assigned || matched.some((l) => l?.assigned);

    // REASSIGNED detection
    const singleWasReassigned =
      lesionAllInfo?.prevClass || lesionAllInfo?.prevType || lesionAllInfo?.predicted_class;

    const matchedWasReassigned = matched.some((l) => l?.prevClass || l?.prevType || l?.predicted_class);

    const wasReassigned = singleWasReassigned || matchedWasReassigned;

    // REASSIGNED CASE

    if (wasReassigned) {
      let tooltipText = t('diagnose.reassigned');

      if (lesionAllInfo?.prevClass || lesionAllInfo?.prevType) {
        if (lesionAllInfo?.prevClass && lesionAllInfo?.prevType) {
          tooltipText = t('diagnose.reassigned_previous', {
            prev_class: getLesionClass(lesionAllInfo?.prevClass, t),
            prev_type: t(`diagnose.${lesionAllInfo?.prevType}`),
          });
        } else {
          tooltipText = t('diagnose.upscale_previous', {
            prev_type:
              lesionAllInfo?.prevType ||
              getLesionClass(lesionAllInfo?.prevClass, t) ||
              getLesionClass(lesionAllInfo?.predicted_class, t) ||
              '-',
          });
        }
      }

      return {
        infoIcon: (
          <Icon
            icon={EditIcon}
            fontSize='medium'
            style={{ width: '16px', height: '16px', color: '#939393' }}
          />
        ),
        infoTooltip: tooltipText,
      };
    }

    // NEW LESION CASE
    if (isNewLesion) {
      return {
        infoIcon: (
          <Icon
            icon={AddIcon}
            fontSize='medium'
            style={{ width: '16px', height: '16px', color: '#939393', transform: 'scaleX(-1)' }}
          />
        ),
        infoTooltip: t('diagnose.added_user'),
      };
    }

    // AI DETECTED (default)
    return {
      infoIcon: (
        <img
          src={modelDetectedIcon}
          alt='model-detected'
          style={{
            width: 16,
            height: 16,
            filter: 'invert(1) grayscale(1)',
            cursor: 'help',
          }}
        />
      ),
      infoTooltip: t('diagnose.ai_detected'),
    };
  };

  // Index content
  const renderIndexContent = () => {
    if (isJustEdited) {
      return (
        <LensIcon
          style={{
            color: theme.palette.accents.main,
            fontSize: 14,
            marginLeft: 2,
          }}
        />
      );
    }

    if (isExpandable) {
      const IconComponent = isExpanded ? ExpandLessIcon : ExpandMoreIcon;

      return (
        <span onClick={toggleExpand} style={{ cursor: 'pointer', display: 'flex' }}>
          <IconComponent
            fontSize='small'
            style={{ fontSize: 16, lineHeight: '16px' }}
            id={isHovered ? 'row-expand-icon' : 'row-expand'}
          />
        </span>
      );
    }

    if (isDraggable) {
      return <DragIndicatorIcon fontSize='small' id='row-drag-icon' />;
    }

    return null;
  };

  // Match / Unmatch
  const renderMatchContent = () => {
    if (isMatchedMainRow) {
      if (isHovered) {
        return (
          <IconButton
            size='small'
            className={classes.row_index_icons}
            onClick={(e) => handleUnmatchClick(e, lesionAllInfo)}>
            <Icon icon={unMatchIcon} className={classes.unmatch_icon} />
          </IconButton>
        );
      }

      return <Icon icon={linkIcon} className={classes.matched_icon} />;
    }

    if (isPossibleMatch) {
      return <Icon icon={possibleLinkIcon} className={`${classes.match_icon} ${classes.link_icon}`} />;
    }

    return null;
  };

  // Previous finding content
  const renderPrevFindingContent = () => {
    return (
      <IconButton
        className={classes.row_index_icons}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onClassChange(lesionAllInfo, lesionAllInfo?.birads);
        }}
        size='small'>
        <AddCircleOutlineIcon fontSize='medium' style={{ width: '16px', height: '16px', color: 'white' }} />
      </IconButton>
    );
  };

  // Tooltips
  const indexTooltip = (() => {
    if (isExpandable) {
      return isExpanded ? t('diagnose.collapse_class') : t('diagnose.expand_class');
    }

    if (matched && !isMatchedMainRow) {
      return `${t('diagnose.drop_new_index')} (ID: ${lesionId})`;
    }

    return `ID: ${lesionId || findingId}`;
  })();

  const matchTooltip = (() => {
    if (isMatchedMainRow || matched) {
      return t('diagnose.drop_new_index_box');
    }
    if (isPossibleMatch) {
      return t('diagnose.drop_box');
    }
    return '';
  })();

  const matchContent = renderMatchContent();
  const extraInfo = renderExtraInfo();
  const previousFinding = lesionAllInfo?.detection_origin === 'prior' && !isClustered;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        minWidth: 48,
        justifyContent: 'space-between',
      }}>
      {/* {(findingId || lesionId) && (
        <Typography
          style={{ fontSize: '11px', whiteSpace: 'nowrap', position: 'absolute', top: 0, left: '2px' }}>
          ID: {findingId} / {lesionId && `[${lesionId}]`}
        </Typography>
      )} */}

      <Tooltip arrow title={<Typography className={classes.tooltip_text}>{indexTooltip}</Typography>}>
        <div className={classes.index_cell_container}>{renderIndexContent()}</div>
      </Tooltip>
      {matchContent && (
        <Tooltip arrow title={<Typography className={classes.tooltip_text}>{matchTooltip}</Typography>}>
          <div className={classes.index_cell_container}>{matchContent}</div>
        </Tooltip>
      )}
      {previousFinding && isHovered && (
        <Tooltip
          title={<Typography className={classes.tooltip_text}>{t('diagnose.reassign_lesion')}</Typography>}
          placement='right-start'
          arrow>
          <div
            id='previous-finding-icon'
            className={`${classes.index_cell_container} classes.previous_finding_icon`}
            style={{ cursor: 'help' }}>
            {renderPrevFindingContent()}
          </div>
        </Tooltip>
      )}
      {extraInfo.infoIcon && (
        <Tooltip
          arrow
          title={<Typography className={classes.tooltip_text}>{extraInfo.infoTooltip}</Typography>}>
          <div className={classes.index_cell_container} style={{ cursor: 'help' }}>
            {extraInfo.infoIcon}
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default memo(IndexCell);
